import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  IQuestionnaireResponse,
  IQuestionnaireSaveResponseBody,
  IQuestionnaireSubmitBody,
} from "../../Interfaces/IQuestionnaireInterface";
import {
  getQuestionnaire,
  saveQuestionnaireResponse,
  submitQuestionnaire,
} from "../../../../services/questionnaireService";
import Banner from "../../components/Banner/Banner";
import Loader from "../../../../components/ui/Loader";
import Questionnaire from "../../components/Questionnaire";
import { QuestionnaireResponses } from "../PublicQuestionnaire/PublicQuestionnaire";
import { Modal } from "@react-solutions-ui/findmore-ui";
import { useNavigate } from "react-router-dom";

export type ModalInfo = {
  title: string;
  content: ReactNode;
  closeAction: () => void;
  closeActionLabel: string;
};

export default function QuestionnairePage() {
  const navigate = useNavigate();
  const evaluatorId = useMemo(() => {
    return new URLSearchParams(window.location.search).get("id");
  }, []);

  const [loading, setLoading] = useState(false);
  const [, setError] = useState<Error | null>(null);
  const [data, setData] = useState<IQuestionnaireResponse | null>(null);

  const [responses, setResponses] = useState<QuestionnaireResponses>({});
  const [completedCategories, setCompletedCategories] = useState<string[]>([]);
  const [savingResponses, setSavingResponses] = useState<boolean>(false);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [modalInfo, setModalInfo] = useState<ModalInfo | null>(null);

  useEffect(() => {
    if (!evaluatorId) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getQuestionnaire(evaluatorId);
        setData(res);
        const answers = Object.fromEntries(
          res.categories.flatMap((category) =>
            category.questions.map((question) => [question.id, question.answer])
          )
        );
        setResponses(answers as QuestionnaireResponses);
        const completed = res.categories
          .filter((category) =>
            category.questions.every(
              (question) => answers[question.id] && answers[question.id] !== ""
            )
          )
          .map((category) => category.categoryName);
        setCompletedCategories(completed);
      } catch (err: any) {
        setError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [evaluatorId]);

  if (loading) return <Loader text="A carregar questionário..." />;
  if (!data) return <div>Nenhum dado recebido.</div>;
  if (data.categories.length === 0)
    return <div>Nenhum questionário encontrado.</div>;

  const handleResponseChange = async (
    questionId: string,
    answer: string,
    type: string
  ) => {
    let newResponses = { ...responses };
    if (answer === "") {
      delete newResponses[questionId];
      setResponses(newResponses);
    } else {
      newResponses[questionId] = answer;
      setResponses(newResponses);
    }
    if (type !== "Text") {
      handleSaveResponses(questionId, answer);
    }
    updateCompletedCategories(newResponses, questionId);
  };

  const updateCompletedCategories = (
    newResponses: QuestionnaireResponses,
    questionId: string
  ) => {
    // Get the category name of the question
    const categoryName = data?.categories.find((category) =>
      category.questions.some((question) => question.id === questionId)
    )?.categoryName;
    // Get all the ids of the questions of the category
    const questionsIds =
      data?.categories
        .find((category) =>
          category.questions.some((question) => question.id === questionId)
        )
        ?.questions.map((question) => question.id) || [];
    // Check how many questions are completed
    const completedQuestions = questionsIds.filter(
      (questionId) => newResponses[questionId]
    );
    if (completedQuestions.length === questionsIds.length) {
      setCompletedCategories((prev) => [...prev, categoryName || ""]);
    }
  };

  const handleSaveResponses = async (questionId: string, answer: string) => {
    if (!data) return;
    setSavingResponses(true);
    try {
      const responseBody: IQuestionnaireSaveResponseBody = {
        EvaluatorId: data.evaluatorId,
        Question: {
          QuestionId: questionId,
          Qualification: answer,
        },
      };
      await saveQuestionnaireResponse(responseBody);
      setLastSavedTime(new Date());
    } catch (err: any) {
      setModalInfo({
        title: "Erro ao guardar resposta",
        content: <p>{err?.message || "Erro ao guardar resposta"}</p>,
        closeAction: () => setModalInfo(null),
        closeActionLabel: "Fechar",
      });
    } finally {
      setSavingResponses(false);
    }
  };

  // Handler to submit all responses
  const handleSubmit = async () => {
    if (!data) return;

    try {
      const submitBody: IQuestionnaireSubmitBody = {
        EvaluatorId: data.evaluatorId,
      };
      await submitQuestionnaire(submitBody);
      navigate("/");
    } catch (err: any) {
      setModalInfo({
        title: "Erro ao submeter questionário",
        content: (
          <p>{err.response.data.error || "Erro ao submeter questionário"}</p>
        ),
        closeAction: () => setModalInfo(null),
        closeActionLabel: "Fechar",
      });
    }
  };

  return (
    <>
      <Banner
        evaluatorType={data.evaluatorType}
        autoEvaluation={data.autoEvaluation}
        evaluatedName={data.evaluatedName}
      />

      <Questionnaire
        data={data.categories}
        responses={responses}
        onResponseChange={handleResponseChange}
        onSubmit={handleSubmit}
        onSaveResponses={handleSaveResponses}
        completedCategories={completedCategories}
        autoEvaluation={data.autoEvaluation}
        evaluatedName={data.evaluatedName}
        savingResponses={savingResponses}
        lastSavedTime={lastSavedTime}
        autoCompleted={data.autoCompleted}
        evaluatorType={data.evaluatorType}
        evaluationId={data.evaluationId}
      />

      {modalInfo && (
        <Modal
          title={modalInfo?.title!}
          type="info"
          onCloseModal={modalInfo?.closeAction!}
          closeModalLabel={modalInfo?.closeActionLabel!}
        >
          {modalInfo?.content!}
        </Modal>
      )}
    </>
  );
}
